import React, { useContext, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useQuery, useLazyQuery } from "@apollo/client"
import _ from "lodash"

import ParticipantSidebar from "../../components/sidebars/ParticipantSidebar"
import PageSidebarContent from "../../components/sidebars/PageSidebarContent"
import { Chart } from "primereact/chart"
import { Dropdown } from "primereact/dropdown"
import { DateTime } from "luxon"
// Context
import { UserContext } from "../../context/userContext"

// Loader
import { LoaderMedium } from "../../components/Loaders"

// Queries
import { ENGAGEMENTS_COUNT, ENGAGEMENTS_CHART } from "./queries"
import styles from "../../styles/Page-Engagements.module.css"

//hooks
import useLabels from "../../hooks/useLabels"

// chart helpers
import { chartOptions } from "./chartOptions"

export default function Engagements() {
  const userCtx = useContext(UserContext)

  const [loadingLabels, labels] = useLabels([
    "recovery_plan_engagement",
    "care_coordination_session",
    "group_session",
    "brief_check_in",
  ])

  const [engagementsCount, setEngagementsCount] = useState({
    brief_check_in: 0,
    care_coordination_session: 0,
    group_session: 0,
    recovery_plan_engagement: 0,
    total_engagements: 0,
  })

  const [dropdownValue, setDropdownValue] = useState({
    name: "All engagements",
    code: "",
  })

  const [engagementTotals, setEngagementsTotals] = useState([])
  const [typeQuery, setTypeQuery] = useState("")

  const { loading } = useQuery(ENGAGEMENTS_COUNT, {
    variables: {
      participant_membership_id: userCtx.participant_membership_id,
      participant_id: userCtx.participant_id,
      organization_id: userCtx.session.user.organization_id,
    },
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      try {
        const parseData = JSON.parse(data.engagementsKpi.data)
        setEngagementsCount(parseData)
      } catch { }
    },
  })

  const [getEngagementChartData] = useLazyQuery(ENGAGEMENTS_CHART, {
    variables: {
      participant_membership_id: userCtx.participant_membership_id,
      numberOfDays: Number(dropdownValue.code),
      year: dropdownValue.name,
      query_type: typeQuery,
    },
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      try {
        if (dropdownValue.name === "All engagements") return
        const parseData = JSON.parse(data.engagementsKpiPerDate.data)
        const dataToArray = _.map(parseData, (value) => value)
        setEngagementsTotals(dataToArray)
      } catch (error) {
        console.error(error)
      }
    },
  })

  function handleAllEngagementsOption() {
    const { first_engagement, ...newEngagementsCount } = engagementsCount

    const allEngagements = Object.entries(newEngagementsCount).map(
      ([key, value]) => value
    )
    return allEngagements
  }

  function handleOnChange({ value }) {
    if (value === undefined) {
      getEngagementChartData({
        variables: {
          participant_membership_id: userCtx.participant_membership_id,
          numberOfDays: 0,
        },
      })
      setEngagementsTotals(handleAllEngagementsOption())
      return setDropdownValue({ name: "All engagements", code: "" })
    }
    if (Number(value.code) > 120) {
      setTypeQuery("year")
    } else {
      setTypeQuery("days")
    }

    if (value.name === "All engagements") {
      setDropdownValue(value)
      return setEngagementsTotals(handleAllEngagementsOption())
    } else {
      setDropdownValue(value)
      getEngagementChartData({
        variables: {
          participant_membership_id: userCtx.participant_membership_id,
          numberOfDays: Number(value.code),
        },
      })
    }
  }

  useEffect(() => {
    setEngagementsTotals(handleAllEngagementsOption())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementsCount])
  
  const handleTableStats = () => {
    return (
      <div className={styles.gridData} style={{ maxWidth: "50%" }}>
        <div>
          <h3>
            <strong>Total number of Engagements</strong>
          </h3>
        </div>
        <div>
          <strong>{engagementsCount.totalEngagements}</strong>
        </div>
        <div>
          <strong>
            <Link
              style={{ color: "#8c28c4" }}
              to="/participant/engagements/brief-checkin"
            >
              {labels.brief_check_in}
            </Link>
          </strong>
        </div>
        <div>{engagementsCount.brief_check_in}</div>
        <div>
          <strong>
            <Link
              style={{ color: "#8c28c4" }}
              to="/participant/engagements/recovery-planning-session"
            >
              {labels.recovery_plan_engagement}
            </Link>
          </strong>
        </div>
        <div>{engagementsCount.recovery_plan_engagement}</div>
        <div>
          <strong>
            <Link style={{ color: "#8c28c4" }} to="/group-session">
              {labels.group_session}
            </Link>
          </strong>
        </div>
        <div>{engagementsCount.group_session}</div>
        <div>
          <strong>
            <Link
              style={{ color: "#8c28c4" }}
              to="/participant/engagements/care-coordination"
            >
              {labels.care_coordination_session}
            </Link>
          </strong>
        </div>
        <div>{engagementsCount.care_coordination_session}</div>
      </div>
    )
  }

  const handleOptions = () => {
    const now = DateTime.now()
    const startOfYear = now.startOf("year")
    const firstEngament = DateTime.fromISO(engagementsCount.first_engagement)
    let defaultOptions = [
      { name: "All engagements", code: "5" },
      { name: "30 days", code: "30" },
      { name: "60 days", code: "60" },
      { name: "90 days", code: "90" },
      { name: "120 days", code: "120" },
    ]

    const daysOfCurrentYear = Math.floor(now.diff(startOfYear, "days").days)
    defaultOptions.push({
      name: `${now.year}`,
      code: `${daysOfCurrentYear}`,
    })

    for (let y = now.year - 1; y >= firstEngament.year; y--) {
      const refDate = DateTime.fromObject({ year: y })
      const daysSince = now.diff(refDate, ["days"]).toObject()
      defaultOptions.push({
        name: `${y}`,
        code: `${Math.floor(daysSince.days)}`,
      })
    }

    return [...defaultOptions]
  }

  const options = handleOptions()

  if (loading || loadingLabels) return <LoaderMedium />

  const charData = {
    labels: [
      labels.brief_check_in,
      labels.recovery_plan_engagement,
      labels.group_session,
      labels.care_coordination_session,
    ],
    datasets: [
      {
        label: "Engagements",
        data: engagementTotals,
        backgroundColor: ["#01b298", "#8c28e4", "#8467ad", "#E48E28"],
        borderWidth: 1,
      },
    ],
  }

  return (
    <>
      <PageSidebarContent>
        <ParticipantSidebar />
      </PageSidebarContent>
      <div className="main-content">
        <div>
          <h1 className={styles.title}>All Engagements</h1>
          {handleTableStats()}
          <br />
          <Dropdown
            value={dropdownValue}
            onChange={handleOnChange}
            showClear
            options={options}
            optionLabel="name"
            filter
            filterBy="name"
            placeholder={dropdownValue.name}
          />
          <br />
          <br />
          <div className="card">
            <Chart type="bar" data={charData} options={chartOptions} />
          </div>
        </div>
      </div>
    </>
  )
}
